
import React, { useState, useEffect } from 'react';
import { TextField, Box, FormControlLabel, Checkbox } from '@mui/material';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  CancelButton,
  SaveButton
} from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../../store';
import { createDocumentType, updateDocumentType } from '../../../../../modules/masterdata/actions';
import { IDocumentType } from '../../../../../modules/masterdata/types';
import { selectCreateDocumentTypeSuccess, selectUpdateDocumentTypeSuccess } from '../../../../../modules/masterdata/selectors';

interface DocumentTypeModalProps {
  open: boolean;
  onClose: () => void;
  documentType?: IDocumentType | null; // If provided, we are in Edit mode
}

const DocumentTypeModal: React.FC<DocumentTypeModalProps> = ({ open, onClose, documentType }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [reqIssueDate, setReqIssueDate] = useState(false);
  const [reqExpiryDate, setReqExpiryDate] = useState(false);
  const [isMandatory, setIsMandatory] = useState(false);

  const isCreateSuccess = useSelector(selectCreateDocumentTypeSuccess);
  const isUpdateSuccess = useSelector(selectUpdateDocumentTypeSuccess);

  useEffect(() => {
    if (documentType) {
      setName(documentType.document_type || '');
      setDescription(documentType.document_description || '');
      setReqIssueDate(!!documentType.req_issue_date);
      setReqExpiryDate(!!documentType.req_expiry_date);
      setIsMandatory(!!documentType.is_mandatory);
    } else {
      setName('');
      setDescription('');
      setReqIssueDate(false);
      setReqExpiryDate(false);
      setIsMandatory(false);
    }
  }, [documentType, open]);

  useEffect(() => {
    if (isCreateSuccess) {
      onClose();
    }
    if (isUpdateSuccess) {
      onClose();
    }
  }, [isCreateSuccess, isUpdateSuccess, onClose]);

  const [nameError, setNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  const handleSave = () => {
    const isNameValid = !!name.trim();
    const isDescriptionValid = !!description.trim();

    setNameError(!isNameValid);
    setDescriptionError(!isDescriptionValid);

    if (!isNameValid || !isDescriptionValid) {
      return;
    }

    if (documentType) {
      dispatch(updateDocumentType({
        id: documentType.document_id,
        document_type: name,
        document_description: description,
        req_issue_date: reqIssueDate,
        req_expiry_date: reqExpiryDate,
        is_mandatory: isMandatory
      }));
    } else {
      dispatch(createDocumentType({
        document_type: name,
        document_description: description,
        req_issue_date: reqIssueDate,
        req_expiry_date: reqExpiryDate,
        is_mandatory: isMandatory
      }));
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledDialogTitle>
        {documentType ? 'Edit Document Type' : 'Create Document Type'}
      </StyledDialogTitle>
      <StyledDialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          required
          fullWidth
          value={name}
          error={nameError}
          helperText={nameError ? 'Name is required' : ''}
          onChange={(e) => {
            setName(e.target.value);
            if (e.target.value.trim()) setNameError(false);
          }}
        />
        <TextField
          margin="dense"
          label="Description"
          required
          fullWidth
          multiline
          rows={3}
          value={description}
          error={descriptionError}
          helperText={descriptionError ? 'Description is required' : ''}
          onChange={(e) => {
            setDescription(e.target.value);
            if (e.target.value.trim()) setDescriptionError(false);
          }}
        />
        <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={reqIssueDate}
                onChange={(e) => setReqIssueDate(e.target.checked)}
                sx={{
                  color: '#5E8E7E',
                  '&.Mui-checked': {
                    color: '#4A7A6A',
                  },
                }}
              />
            }
            label="Required Issue Date"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={reqExpiryDate}
                onChange={(e) => setReqExpiryDate(e.target.checked)}
                sx={{
                  color: '#5E8E7E',
                  '&.Mui-checked': {
                    color: '#4A7A6A',
                  },
                }}
              />
            }
            label="Required Expiry Date"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isMandatory}
                onChange={(e) => setIsMandatory(e.target.checked)}
                sx={{
                  color: '#5E8E7E',
                  '&.Mui-checked': {
                    color: '#4A7A6A',
                  },
                }}
              />
            }
            label="Is Mandatory"
          />
        </Box>
      </StyledDialogContent>
      <StyledDialogActions>
        <CancelButton onClick={onClose}>
          Cancel
        </CancelButton>
        <SaveButton onClick={handleSave} variant="contained" disableElevation>
          {documentType ? 'Save Changes' : 'Create'}
        </SaveButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default DocumentTypeModal;
